import { inject, Injectable } from '@angular/core';
import { HttpService } from '@okcargo-apps/shared-infrastructure';
import { BehaviorSubject, catchError, map, Observable, of, take, tap } from 'rxjs';
import { ICountry, IState } from '../../../../shared-domain-ionic/src/lib/address/country-state.model';

@Injectable({
  providedIn: 'root',
})
export class CountryStateService {
  private readonly http = inject(HttpService);

  private readonly countriesSubject = new BehaviorSubject<ICountry[]>([]);
  private readonly statesSubject = new BehaviorSubject<IState[]>([]);
  private readonly currentCountryCodeSubject = new BehaviorSubject<string | null>(null);

  constructor() {
    this.loadCountries();
  }

  get countries$(): Observable<ICountry[]> {
    return this.countriesSubject.asObservable();
  }

  public searchStatesByCountryCode$(countryIsoCode: string): Observable<IState[]> {
    if (this.currentCountryCodeSubject.value === countryIsoCode) {
      return this.statesSubject.asObservable();
    }

    return this.loadStatesForCountry$(countryIsoCode);
  }

  public findStateByCode$(countryCode: string, stateCode: string): Observable<IState | undefined> {
    return this.searchStatesByCountryCode$(countryCode).pipe(
      take(1), // IMPORTANT: Complete the observable after emitting the first value
      map((states) => states.find((state) => state.stateCode === stateCode)),
    );
  }

  public findCountryByCode$(isoCode: string): Observable<string> {
    return this.countries$.pipe(
      take(1), // IMPORTANT: Complete the observable after emitting the first value
      map((countries) => countries.find((c) => c.isoCode === isoCode)?.name || ''),
    );
  }

  private loadCountries(): void {
    this.http
      .get<ICountry[]>('/assets/country-state/countries.json')
      .pipe(
        catchError((error) => {
          console.error('Error loading countries', error);
          return of([]);
        }),
      )
      .subscribe((countries) => {
        this.countriesSubject.next(countries);
      });
  }

  private loadStatesForCountry$(countryIsoCode: string): Observable<IState[]> {
    return this.http.get<IState[]>(`/assets/country-state/${countryIsoCode}.json`).pipe(
      tap((states) => {
        this.statesSubject.next(states);
        this.currentCountryCodeSubject.next(countryIsoCode);
      }),
      catchError((error) => {
        console.error(`Error loading states for country ${countryIsoCode}`, error);
        this.statesSubject.next([]);
        this.currentCountryCodeSubject.next(null);
        return of([]);
      }),
    );
  }
}
